/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import React, { useEffect, useRef } from 'react'
import { defineMessages } from 'react-intl'
import { Link, Outlet, useLocation } from 'react-router-dom'
import cx from 'classnames'
import { css, jsx } from '@emotion/react'
import { useDispatch } from 'react-redux'

import {
  EuiCallOut,
  EuiErrorBoundary,
  EuiPage,
  EuiPageBody,
  EuiPageSection,
  EuiPageTemplate,
  EuiText,
  useEuiTheme,
  withEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { themedBoxShadow } from '@modules/cui/styles/boxShadow'
import { BreadcrumbsContext } from '@modules/cui/Breadcrumbs'
import { logoutUrl } from '@modules/auth/urls'
import { useEulaStateQuery } from '@modules/eula/hooks'
import UnhandledApplicationLoadError from '@modules/app/UnhandledApplicationLoadError'
import { getResponseStatus } from '@modules/utils/ajax'

import SchedulerMiddleware from '@/lib/schedule/SchedulerMiddleware'
import { fetchEolStatusIfNeeded } from '@/actions/eolStatus'
import { fetchCurrentUser, resetFetchCurrentUser } from '@/actions/currentUser'
import { ErrorNotification } from '@/components/ErrorNotification'
import { getConfigForKey } from '@/store'

import AppLoadingRoot from '../../../../components/AppLoadingRoot'
import ChromeNavigation from '../../../../components/ChromeNavigation'
import ChromeHeader from '../../../../components/ChromeHeader'
import RefreshApiToken from '../../../../components/RefreshApiToken'
import Eula from '../../../../components/Eula'
import { EnsureApiBaseUrl } from '../../../../components/ApiBaseUrl'
import PhoneHomeData from '../../../../components/PhoneHome/Data'

import type { AllProps } from './types'

const messages = defineMessages({
  rootFailed: {
    id: `app-index.fetching-the-root-resource-failed-link`,
    defaultMessage: `Fetching the root resource failed. {logOut}`,
  },
  permissionsFailed: {
    id: `app-index.fetching-user-permissions-failed-link`,
    defaultMessage: `Fetching user permissions failed. {logOut}`,
  },
  logOut: {
    id: `app-index.log-out`,
    defaultMessage: `Log out`,
  },
})

const App: React.FunctionComponent<AllProps> = ({
  fetchCurrentUserRequest,
  requireEula,
  currentUser,
  isCreateUrl,
  isGettingStartedUrl,
  isSaasAdminconsole,
}) => {
  const theme = useEuiTheme()
  const dispatch = useDispatch()
  const { pathname } = useLocation()

  const breadcrumbsRef = useRef<HTMLDivElement>(null)
  const announcementsBreadcrumbsRef = useRef<HTMLDivElement>(null)

  const eulaStatusQuery = useEulaStateQuery({ enabled: requireEula })

  useEffect(() => {
    dispatch(fetchEolStatusIfNeeded())

    if (!currentUser) {
      dispatch(fetchCurrentUser())
    }

    return () => {
      dispatch(resetFetchCurrentUser())
    }
  }, [currentUser, dispatch])

  if (requireEula && eulaStatusQuery.isError) {
    return (
      <UnhandledApplicationLoadError error={eulaStatusQuery.error as Error}>
        <FormattedMessage
          id='app-index.fetching-the-root-resource-failed-link'
          defaultMessage='Fetching the root resource failed. {logOut}'
          values={{
            logOut: (
              <Link to={logoutUrl()} state={{ from: pathname }}>
                <FormattedMessage id='app-index.log-out' defaultMessage='Log out' />
              </Link>
            ),
          }}
        />
      </UnhandledApplicationLoadError>
    )
  }

  if (fetchCurrentUserRequest.error && getResponseStatus(fetchCurrentUserRequest.error) !== 401) {
    // 401 handed by diverting to `/logout`; no need to show an error for it
    return (
      <UnhandledApplicationLoadError error={fetchCurrentUserRequest.error as Error | undefined}>
        <FormattedMessage
          {...messages.permissionsFailed}
          values={{
            logOut: (
              <Link to={logoutUrl()} state={{ from: pathname }}>
                <FormattedMessage id='app-index.log-out' defaultMessage='Log out' />
              </Link>
            ),
          }}
        />
      </UnhandledApplicationLoadError>
    )
  }

  if (requireEula && eulaStatusQuery.isSuccess && !eulaStatusQuery.data.accepted) {
    return <Eula />
  }

  if (!currentUser) {
    return <AppLoadingRoot />
  }

  const isGettingStartedRoute = isCreateUrl || isGettingStartedUrl

  const cloudContentStyle = css`
    background: ${theme.euiTheme.colors.emptyShade};
    box-shadow: ${themedBoxShadow({ theme })};
  `
  const cloudContentGettingStartedStyle = css`
    background: ${theme.euiTheme.colors.body};
    box-shadow: ${themedBoxShadow({ theme })};
    min-height: 100%;
    box-shadow: none;
    padding-top: 0;
  `

  const cloudContentBodyStyle = {
    maxWidth: 1320,
    margin: '0 auto',
    border: 'none',
    padding: 0,
    boxShadow: 'none',
  }
  const isProduction = getConfigForKey(`CLOUD_UI_ENV`) === `production`
  const showProductionBanner = isSaasAdminconsole && isProduction

  return (
    <SchedulerMiddleware>
      <EnsureApiBaseUrl>
        <EuiPageTemplate
          css={css`
            .euiCollapsibleNavButtonWrapper {
              display: none;
            }
          `}
        >
          {showProductionBanner && (
            <EuiCallOut
              color='danger'
              css={css({
                backgroundColor: 'red',
                padding: '8px',
              })}
            >
              <EuiText color='white' textAlign='center'>
                <FormattedMessage
                  id='adminconsole.app.production-banner'
                  defaultMessage='PRODUCTION'
                />
              </EuiText>
            </EuiCallOut>
          )}
          <ChromeHeader
            breadcrumbsRef={breadcrumbsRef}
            announcementsRef={announcementsBreadcrumbsRef}
          />
          <ErrorNotification />
          <EuiPage paddingSize='none'>
            {!isGettingStartedRoute && (
              <aside
                css={css`
                flex: 1,
                min-width: 15rem;
                max-width: 15rem;
                padding: ${theme.euiTheme.size.l};

                @media only screen and (max-width: ${theme.euiTheme.breakpoint.m}px) {
                  padding: 0;
                  max-width: none;
                  border-right: none;
                  margin-bottom: -0.5625rem;
                }
              `}
              >
                <ChromeNavigation />
              </aside>
            )}

            <div
              className={cx('cloudContent', { createPage: isGettingStartedRoute })}
              id='cloudPortalPage'
              css={isGettingStartedRoute ? cloudContentGettingStartedStyle : cloudContentStyle}
            >
              <EuiPageBody>
                <EuiPageSection
                  css={cloudContentBodyStyle}
                  className='cloudContentBody'
                  paddingSize='m'
                  color='transparent'
                >
                  <div data-app='appContentBody'>
                    <PhoneHomeData />

                    <EuiErrorBoundary>
                      <BreadcrumbsContext.Provider
                        value={{
                          breadcrumbsRef,
                          announcementsRef: announcementsBreadcrumbsRef,
                        }}
                      >
                        <Outlet />
                      </BreadcrumbsContext.Provider>
                    </EuiErrorBoundary>
                  </div>
                </EuiPageSection>
              </EuiPageBody>
            </div>
          </EuiPage>
        </EuiPageTemplate>
      </EnsureApiBaseUrl>

      <RefreshApiToken />
    </SchedulerMiddleware>
  )
}

export default withEuiTheme(App)
